import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslations } from "use-intl";
import { type Project, QuizType } from "$/modules/appwrite/types/collections";
import { ExamSettingsCard } from "$/modules/dashboard/ui/exam-settings-card";
import { LiveBattleCard } from "$/modules/dashboard/ui/live-battle-card";
import { ProjectKpis } from "$/modules/dashboard/ui/project-kpis";
import { ExamManager } from "$/modules/exam/ui/exam-manager";
import { updateProjectQuizType } from "$/modules/projects/lib/projects";
import {
	dashboardStatsQueryOptions,
	projectQueryOptions,
} from "$/modules/projects/lib/queries";
import { ShareQuizModal } from "$/modules/projects/ui/share-quiz-modal";
import { useServerFnHandler } from "$/modules/shared/hooks/useServerFnHandler";
import { Card, CardContent } from "$/modules/shared/ui/card";
import { ConstructionHeader } from "$/modules/shared/ui/construction-header";
import { Field } from "$/modules/shared/ui/field";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "$/modules/shared/ui/select";

export const Route = createFileRoute(
	"/$locale/$userId/projects/$projectId/dashboard/",
)({
	component: DashboardPage,
	loader: async ({ params, context: { queryClient } }) => {
		void queryClient.prefetchQuery(
			dashboardStatsQueryOptions(params.userId, params.projectId),
		);
		await queryClient.ensureQueryData({
			...projectQueryOptions(params.userId, params.projectId),
			revalidateIfStale: true,
		});
	},
});

function DashboardPage() {
	const { locale, userId, projectId } = Route.useParams();
	const options = projectQueryOptions(userId, projectId);
	const { data: project } = useSuspenseQuery(options);
	const queryClient = useQueryClient();
	const updateProject = (patch: Partial<Project>) => {
		queryClient.setQueryData(options.queryKey, (current) =>
			current ? { ...current, ...patch } : current,
		);
	};

	return (
		<>
			<ConstructionHeader
				titleKey="project.sidebar.dashboard"
				description={project?.name}
			>
				{(project.questionnaires?.length ?? 0) > 0 && (
					<ShareQuizModal project={project} />
				)}
			</ConstructionHeader>

			<ProjectKpis project={project} />

			<QuizTypeControl
				key={project.quizType}
				project={project}
				onTypeChanged={(quizType) => updateProject({ quizType })}
			/>

			{project.quizType === QuizType.Exam && (
				<ExamSettingsCard
					project={project}
					onSaved={(settings) => updateProject(settings)}
				/>
			)}

			{project.quizType === QuizType.Exam && (
				<ExamManager project={project} locale={locale} userId={userId} />
			)}

			{project.quizType === QuizType.GameShow && (
				<LiveBattleCard project={project} locale={locale} />
			)}
		</>
	);
}

function QuizTypeControl({
	project,
	onTypeChanged,
}: {
	project: Project;
	onTypeChanged: (quizType: QuizType) => void;
}) {
	const t = useTranslations();
	const { triggerAsyncFunction, isPending } = useServerFnHandler();
	const [quizType, setQuizType] = useState(project.quizType);
	const labels: Record<QuizType, string> = {
		[QuizType.Survey]: t("quizTypes.survey"),
		[QuizType.GameShow]: t("quizTypes.gameShow"),
		[QuizType.Exam]: t("quizTypes.exam"),
	};

	const changeType = (nextType: QuizType) => {
		if (nextType === quizType) return;
		const previousType = quizType;
		setQuizType(nextType);
		triggerAsyncFunction(
			() => updateProjectQuizType({ id: project.$id, quizType: nextType }),
			() => onTypeChanged(nextType),
			() => setQuizType(previousType),
		);
	};

	return (
		<Card className="mt-6 border border-default-200">
			<CardContent className="gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<p className="text-xs font-extrabold uppercase tracking-[0.16em] text-primary">
						{t("projectMode.modeLabel")}
					</p>
					<h2 className="font-display text-xl font-bold">
						{t("projectMode.typeTitle")}
					</h2>
					<p className="text-sm text-foreground-500">
						{t("projectMode.typeDescription")}
					</p>
				</div>
				<Field className="sm:max-w-56">
					<Select
						aria-label={t("projectMode.typeTitle")}
						value={[quizType][0]}
						onValueChange={(value) =>
							((value: string) => changeType(value as QuizType))(value)
						}
						disabled={isPending}
					>
						<SelectTrigger className="w-full">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectGroup>
								{Object.entries(labels).map(([key, label]) => (
									<SelectItem value={key}>{label}</SelectItem>
								))}
							</SelectGroup>
						</SelectContent>
					</Select>
				</Field>
			</CardContent>
		</Card>
	);
}
